import React, { forwardRef } from 'react';

import styledC, { keyframes } from 'styled-components';

import { useInstanceId } from '@adsk/alloy-react-helpers';

import { ComponentIconProps } from '../types';

const spin = keyframes`
  100% {
    transform: rotate(360deg);
  }
`;

const SpinningContainer = styledC.div<{ $indeterminate: boolean }>`
  display: inline-block;
  animation: ${(props) => (props.$indeterminate ? spin : 'none')} 0.6s linear
    infinite;
`;

interface AlloyProgressRingProps extends ComponentIconProps {
  indeterminate?: boolean;
  now?: number;
  thickness?: number;
  colorDark?: string;
  colorLight?: string;
  alternative?: boolean;
}

const AlloyProgressRing = forwardRef<SVGSVGElement, AlloyProgressRingProps>(
  (
    {
      size = 24,
      thickness = 4,
      colorDark: _colorDark,
      colorLight: _colorLight,
      indeterminate = true,
      now,
      alternative = false,
      className,
    },
    ref,
  ) => {
    const instanceId = useInstanceId();
    const radius = (size - thickness) / 2;
    const circumference = 2 * Math.PI * radius;
    const fixedCircumference = +circumference.toFixed(3);

    return (
      <SpinningContainer
        className={className}
        $indeterminate={indeterminate}
        style={{
          width: size,
          height: size,
          transform: indeterminate ? 'rotate(0deg)' : 'rotate(-90deg)',
        }}
      >
        <svg
          ref={ref}
          width={size}
          height={size}
          viewBox={`${size / 2} ${size / 2} ${size} ${size}`}
          style={{ display: 'block' }}
        >
          <defs>
            <linearGradient
              gradientTransform="rotate(270)"
              id={`${instanceId}-gradient`}
              x1="50%"
              y1="100%"
              x2="50%"
              y2="0%"
            >
              <stop offset="0%" stopOpacity="0" stopColor="currentColor" />
              <stop offset="100%" stopOpacity="1" stopColor="currentColor" />
            </linearGradient>
          </defs>
          <circle
            cx={size}
            cy={size}
            r={radius}
            fill="none"
            strokeWidth={thickness}
            stroke={
              alternative ? 'rgba(255, 255, 255, 0.25)' : 'rgba(0, 0, 0, .05)'
            }
          />
          <circle
            cx={size}
            cy={size}
            r={radius}
            fill="none"
            strokeWidth={thickness}
            stroke={
              indeterminate ? `url(#${instanceId}-gradient)` : 'currentColor'
            }
            strokeLinecap="round"
            strokeDasharray={fixedCircumference}
            strokeDashoffset={
              indeterminate
                ? fixedCircumference / 2
                : ((100 - Math.min(now ?? 0, 100)) / 100) * fixedCircumference
            }
            style={{
              transition: indeterminate
                ? 'none'
                : 'stroke-dashoffset linear 0.2s',
            }}
          />
        </svg>
      </SpinningContainer>
    );
  },
);

AlloyProgressRing.displayName = 'AlloyProgressRing';

export default AlloyProgressRing;
